import { environment } from '../../config/environment';
import { useApiClient } from '../api';

export const useCelloService = () => {
    const apiClient = useApiClient();
  /**
   * Get an authentication token for Cello
   * @param traits User traits to include in the token
   * @returns Promise with token response
   */
  const getAuthToken = async (traits?: Record<string, unknown>) => {
    try {
      const response = await apiClient.post(
        `${environment.expressApiUrl}/api/cello/token`,
        traits
      );
      return response.data;
    } catch (error) {
      console.error('[Cello API] Error getting auth token:', error);
      throw error;
    }
  };

  /**
   * Track an event in Cello
   * @param event Event name
   * @param properties Event properties
   * @returns Promise with API response
   */
  const trackEvent = async (event: string, properties?: Record<string, unknown>) => {
    try {
      const response = await apiClient.post(
        `${environment.expressApiUrl}/api/cello/event`,
        { event, properties }
      );
      return response.data;
    } catch (error) {
      console.error('[Cello API] Error tracking event:', error);
      throw error;
    }
  };

  /**
   * Track a page view in Cello
   * @param page Page name
   * @param properties Page properties
   * @returns Promise with API response
   */
  const trackPageView = async (page: string, properties?: Record<string, unknown>) => {
    try {
      const response = await apiClient.post(
        `${environment.expressApiUrl}/api/cello/pageview`,
        { page, properties }
      );
      return response.data;
    } catch (error) {
      console.error('[Cello API] Error tracking page view:', error);
      throw error;
    }
  };

  /**
   * Completes the signup process by notifying the backend to send Cello events
   * and create/update the Stripe customer.
   * 
   * @param ucc The Universal Cello Code obtained from Cello SDK.
   * @param firebaseUserId The Firebase UID of the newly signed-up user.
   * @param email The email of the user.
   * @param displayName The display name of the user.
   * @param ctUserId The CoinTracking user ID (can be used as productUserId or organizationId).
   * @param currency The user's currency (e.g., from currentCtUser.currency).
   * @returns Promise with API response.
   */
  const completeSignupProcess = async (
    ucc: string,
    firebaseUserId: string,
    email: string | null,
    displayName: string | null,
    currency: string
  ) => {
    try {
      const payload = {
        ucc,
        firebaseUserId,
        email,
        displayName,
        currency,
      };
      const response = await apiClient.post(
        `${environment.expressApiUrl}/api/cello/complete-signup`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error('[Cello API] Error completing signup process:', error);
      throw error;
    }
  };

  return {
    getAuthToken,
    trackEvent,
    trackPageView,
    completeSignupProcess
  };
};
